import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  useTheme
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageSwitcher = () => {
  const theme = useTheme();
  const { currentLanguage, changeLanguage, availableLanguages } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract language from the current URL path
  const match = location.pathname.match(/^\/(en|fr)(\/|$)/);
  const currentLang = match ? match[1] : 'en';

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    
    // Update the URL to reflect the new language
    const currentPath = location.pathname;
    
    // Replace the current language in the URL with the new one
    const newPath = currentPath.replace(`/${currentLang}`, `/${languageCode}`);
    
    // If we're not on a language-aware route, add the language prefix
    if (!currentPath.startsWith('/en') && !currentPath.startsWith('/fr')) {
      navigate(`/${languageCode}${currentPath}`);
    } else {
      navigate(newPath);
    }
    
    handleClose();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Button
        onClick={handleClick}
        startIcon={<LanguageIcon />}
        sx={{
          color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          {availableLanguages.find(lang => lang.code === currentLanguage)?.flag} {currentLang.toUpperCase()}
        </Typography>
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 150,
            mt: 1,
          },
        }}
      >
        {availableLanguages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === currentLanguage}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main + '20',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '30',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <Typography variant="h6">{language.flag}</Typography>
            </ListItemIcon>
            <ListItemText
              primary={language.name}
              primaryTypographyProps={{
                fontWeight: language.code === currentLanguage ? 600 : 400,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher; 